import React from 'react';
import { ReportTheme } from '../../types';

interface DailyVisitApiWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
  aggregation?: 'sum' | 'average';
  metric?: string;
}

export const DailyVisitApiWidget: React.FC<DailyVisitApiWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme,
  aggregation = 'average',
  metric = 'Metric'
}) => {

  // Process the raw API data internally
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('DailyVisitApiWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    
    if (!rawData || Object.keys(rawData).length === 0) {
      console.warn('DailyVisitApiWidget: No data entries found');
      return null;
    }
    
    // Filter data by date range if dates are provided
    let filteredData: { [key: string]: any } = {};
    if (report_start_date && report_end_date) {
      const startDate = new Date(report_start_date);
      const endDate = new Date(report_end_date);
      
      Object.keys(rawData).forEach(dateKey => {
        const dayData = rawData[dateKey];
        if (!dayData || typeof dayData !== 'object') {
          return;
        }
        // Convert dateKey (e.g., "20240912") to Date object
        const year = parseInt(dateKey.substring(0, 4));
        const month = parseInt(dateKey.substring(4, 6)) - 1; // Month is 0-indexed
        const day = parseInt(dateKey.substring(6, 8));
        const entryDate = new Date(year, month, day);
        
        if (entryDate >= startDate && entryDate <= endDate) {
          filteredData[dateKey] = dayData;
        }
      });
      
      console.log(`DailyVisitApiWidget: Filtered ${Object.keys(filteredData).length} entries from ${Object.keys(rawData).length} total entries for date range ${report_start_date} to ${report_end_date}`);
    } else {
      filteredData = rawData;
    }
    
    if (Object.keys(filteredData).length === 0) {
      console.warn('DailyVisitApiWidget: No data entries found in date range');
      return null;
    }

    // Get the metric key from data_points[0]
    const metricKey = data_points && data_points[0] ? data_points[0] : null;
    if (!metricKey) {
      console.warn('DailyVisitApiWidget: No data_points provided');
      return null;
    }

    let totalValue = 0;
    let totalSessions = 0;
    let entryCount = 0;


    Object.keys(filteredData).forEach(dateKey => {
      const dayData = filteredData[dateKey];
      if (!dayData || typeof dayData !== 'object') {
        return;
      }
        let metricValue = dayData[metricKey];
        let sessions = dayData.sessions;
        // Process each entry in the day's data
        
        if (aggregation === 'sum') {
            // For sum aggregation, just add the values
            totalValue += metricValue;
            totalSessions += sessions;
        } else if (aggregation === 'average') {
            // For average aggregation, use weighted average based on sessions
            if (sessions > 0) {
                totalValue += metricValue * sessions;
                totalSessions += sessions;
            }
        }
        entryCount++;
    });

    if (entryCount === 0) {
      console.warn(`DailyVisitApiWidget: No valid ${metricKey} data found`);
      return null;
    }

    let finalValue: number;
    let displayValue: string;
    let unit: string = '';

    if (aggregation === 'sum') {
      finalValue = totalValue;
      displayValue = totalValue.toLocaleString();
    } else {
      // Average aggregation
      finalValue = totalSessions > 0 ? totalValue / totalSessions : 0;
      
      // Format based on metric type
      if (metricKey === 'avg_session_duration') {
        // Convert seconds to minutes and seconds
        const minutes = Math.floor(finalValue / 60);
        const seconds = Math.floor(finalValue % 60);
        displayValue = `${minutes}m ${seconds}s`;
        unit = 'time';
      } else if (metricKey === 'bounce_rate') {
        // Convert to percentage
        displayValue = `${(finalValue * 100).toFixed(1)}%`;
        unit = 'percentage';
      } else {
        // Default number formatting
        displayValue = finalValue.toLocaleString();
      }
    }

    return {
      value: finalValue,
      displayValue,
      unit,
      metricKey,
      entryCount,
      totalSessions
    };
  };
  const processedData = processData();
  

  if (!processedData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="text-center">
        <div className="text-3xl font-bold text-foreground mb-2">
          {processedData.displayValue}
        </div>
        <div className="text-sm text-muted-foreground">
          {metric}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {aggregation === 'sum' 
            ? `Total from ${processedData.entryCount} entries`
            : `Average from ${processedData.totalSessions.toLocaleString()} sessions`
          }
        </div>
      </div>
    </div>
  );
};
